import { AfterViewInit, Component, ElementRef, NgZone, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { finalize } from 'rxjs/operators';
import { AuthService } from '../../shared/auth/auth.service';
import { appAuthConfig } from '../../shared/auth/app-auth.config';

type GoogleCredentialResponse = {
  credential: string;
};

type GoogleIdConfiguration = {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
};

type GoogleButtonConfiguration = {
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  width?: string | number;
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
};

type GoogleIdentity = {
  accounts: {
    id: {
      initialize: (config: GoogleIdConfiguration) => void;
      renderButton: (parent: HTMLElement, options: GoogleButtonConfiguration) => void;
    };
  };
};

declare global {
  interface Window {
    google?: GoogleIdentity;
  }
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent implements AfterViewInit {
  @ViewChild('googleButtonHost', { static: true })
  private googleButtonHost?: ElementRef<HTMLDivElement>;

  readonly allowedDomain = appAuthConfig.allowedDomain;

  loading = false;
  errorMessage = '';

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    if (!appAuthConfig.googleClientId.trim()) {
      this.errorMessage = 'Google client ID is missing. Configure it before using login.';
      return;
    }

    void this.initializeGoogleSignIn();
  }

  private async initializeGoogleSignIn(): Promise<void> {
    try {
      await ensureGoogleScriptLoaded();
    } catch {
      this.errorMessage = 'Failed to load Google Sign-In script. Please refresh and try again.';
      return;
    }

    if (!window.google || !this.googleButtonHost) {
      this.errorMessage = 'Google Sign-In is unavailable in this browser.';
      return;
    }

    const hostWidth = Math.floor(this.googleButtonHost.nativeElement.getBoundingClientRect().width);
    const buttonWidth = Math.min(360, Math.max(240, hostWidth || 320));

    window.google.accounts.id.initialize({
      client_id: appAuthConfig.googleClientId,
      callback: (response) => this.ngZone.run(() => this.onGoogleCredential(response)),
      auto_select: false,
      cancel_on_tap_outside: true
    });

    window.google.accounts.id.renderButton(this.googleButtonHost.nativeElement, {
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      width: buttonWidth
    });
  }

  private onGoogleCredential(response: GoogleCredentialResponse): void {
    if (!response.credential) {
      this.errorMessage = 'Google did not return an ID token.';
      return;
    }

    this.errorMessage = '';
    this.loading = true;

    this.authService
      .exchangeGoogleToken(response.credential)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
          const targetUrl = returnUrl && returnUrl.startsWith('/') ? returnUrl : '/feed';
          void this.ngZone.run(() => this.router.navigateByUrl(targetUrl));
        },
        error: (error: unknown) => {
          this.errorMessage = this.extractSignInErrorMessage(error);
        }
      });
  }

  private extractSignInErrorMessage(error: unknown): string {
    if (!(error instanceof HttpErrorResponse)) {
      return 'Sign-in failed. Please try again.';
    }

    if (error.status === 403) {
      return `Sign-in failed. Only ${this.allowedDomain} accounts are allowed.`;
    }

    const details = error.error as { errors?: Record<string, string[]> } | null;
    const firstError = details?.errors
      ? Object.values(details.errors).flat().find((message) => !!message?.trim())
      : null;

    if (firstError) {
      return firstError;
    }

    if (error.status >= 500) {
      return 'Sign-in failed due to a server error. Check API logs and retry.';
    }

    return 'Sign-in failed. Please verify configuration and try again.';
  }
}

function ensureGoogleScriptLoaded(): Promise<void> {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src="https://accounts.google.com/gsi/client"]'
    );

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject();
    document.head.appendChild(script);
  });
}
